interface VersionDefinition {
    year: number;
    month: number;
    iteration: number;
}

interface ChangesDefinition {
    features?: string[];
    bugfixes?: string[];
}

class Version {
    version: VersionDefinition;
    changes: ChangesDefinition;

    constructor(version: VersionDefinition, changes: ChangesDefinition) {
        this.version = version;
        this.changes = changes;
    }

    toString(): string {
        return `${this.version.year}.${this.version.month}.${this.version.iteration}`;
    }
}

export const Versions: Version[] = [
    new Version(
        {
            year: 2025,
            month: 1,
            iteration: 1,
        },
        {
            features: [
                "Implement new reporting features powered by Power BI",
                "Improve the wording on the invoice table headers",
                "Improve the wording on the invoice table finalize/unfinalize buttons",
                "Add back buttons in the page title areas",
            ],
            bugfixes: [
                "Change Save and Add New button on edit dialogs to Save",
                "Make the unfinalize button on the invoice table admin only",
            ],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 12,
            iteration: 2,
        },
        {
            features: [
                "Restructures invoice potential items",
                "Implemented 3 state checkboxes to replace All/Yes/No dropdowns",
                "Added Visible and Reimbursable filters to entry searches",
                "Added Next/Previous buttons to entry dialogs",
                "Added optional requirement for WP Code in entry dialogs",
                "Added additional functionality to filter date controls",
                "Add Save and Close/Save and Add New buttons to several dialogs",
                "Added filter persistence",
            ],
            bugfixes: [
                "Fixed an issue preventing staff from being updated correctly",
                "Fixed an issue preventing assignment billing from being saved correctly",
            ],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 12,
            iteration: 1,
        },
        {
            features: [
                "Restructured and improved entry filters",
                "Updated confirmation flow for updating/deleting posted entries",
                "Display 'No Data' for tables with no content",
            ],
            bugfixes: [
                "Fixed an cache invalidation issue when updating a client",
                "Fixed an issue which prevented outside consultants from updating non-posted entries",
                "Fixed an issue preventing invoices from being created correctly",
                "Fixed incorrect table data on the invoices table",
            ],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 11,
            iteration: 4,
        },
        {
            bugfixes: ["Fixed an issue causing the is posted warning to show up for non-posted entries"],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 11,
            iteration: 3,
        },
        {
            features: [
                "Refined the default work location components",
                "Added the ability for admins to reset staff passwords when needed",
                "The time/expense form fields dynamically change based on the entry type",
                "Adding time/expense entries now prepopulates the type of entry",
                "Added the expense subcategory accounting detail functionalities",
                "Added the expense subcategory default rates functionalities",
                "Assignments roster now only shows active staff members",
            ],
            bugfixes: ["Fixed an issue where the assignment roster checkboxes would not correctly populate"],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 11,
            iteration: 2,
        },
        {
            features: [
                "Optimized data fetching from setup and lookups",
                "Added assignments columns to entries tables",
                "Implemented new currency form field",
                "Added activate/deactivate indicators",
                "Added confirmation modal for all delete actions",
            ],
            bugfixes: ["Fix sort by mutators when sort by numbers are not distinct"],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 11,
            iteration: 1,
        },
        {
            features: [
                "Migrated all tables to new custom library",
                "Added show password toggle to the password fields",
                "Added forgot password page",
                "Added typing autocomplete to the client dropdowns",
                "Added sorting to the staff assignment roster",
                "Added confirm dialog for when admins attempt to perform an action on a posted entry",
                "Add conditional display to the locations and phases fields in the entry filters",
                "Added confirmations dialog to delete actions",
                "Removed frontend API parameters for user and time update tracking",
            ],
            bugfixes: [
                "Fixed roster insert",
                "Disable action buttons on posted entries for non-administrator users",
                "Made button sizes consistent",
                "Fixed auth guards to allow authenticated users access to entry pages",
            ],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 10,
            iteration: 3,
        },
        {
            features: [
                "Changed default entries start date to 2 weeks prior to today",
                "Updated assignments page to support new clients endpoint changes",
                "Refactor assignments to use `cityStates` instead of `workLocationsList`",
                "Refactored icon buttons for more reliable use",
                "Added activate/deactivate quick actions to all tables",
                "Added confirm dialog to activate/deactivate quick actions",
            ],
            bugfixes: ["Fixed intermittent error when populated checkbox values"],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 10,
            iteration: 2,
        },
        {
            features: [
                "Updated the view/edit entry modal's staff select to only show active staff members",
                "Removed extraneous API calls",
            ],
            bugfixes: [
                "Fixed an issue where email confirmation would call the API twice",
                "Fixed an issue causing the view/edit entry modal to not show correct cost amounts",
                "Fixed an issue causing the view/edit entry modal's posted field to intermittently not populate",
            ],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 10,
            iteration: 1,
        },
        {
            features: [
                "Add staff name to multi-staff entry search results",
                "Add staff active filtering to entry filters",
            ],
        },
    ),
    new Version(
        {
            year: 2024,
            month: 9,
            iteration: 1,
        },
        {
            features: [
                "Implemented change log",
                "Added totals across categories",
                "Set default rate type for outside consultants",
                "Added confirm email page",
                "Improved error message handling",
                "Implemented change password",
                "User profile management",
                "Condensed dialogs",
                "Remove staff number",
                "Changed dialogs to stay at a constant position",
                "Added default filters for outside consultants",
                "Added assignments > budget tab",
            ],
            bugfixes: [
                "Updated totals to be rate type specific",
                "Fixed error when closing the staff dialog",
                "Hid specific filters for outside consultants",
                "Fixed an error for staff grid query invalidation",
                "Fixed sizing issue for the staff edit dialog",
            ],
        },
    ),
] as const;
